class OmbrazJournal extends HTMLElement {
  constructor() {
    super();
    this.pageSize = Number(this.dataset.pageSize) || 8;
    this.mockPaginationPages = Number(this.dataset.mockPaginationPages) || 0;
    this.cards = Array.from(this.querySelectorAll('[data-journal-card]'));
    this.grid = this.querySelector('[data-journal-grid]');
    this.form = this.querySelector('[data-journal-form]');
    this.searchInput = this.querySelector('[data-journal-search]');
    this.sortInput = this.querySelector('[data-journal-sort]');
    this.sortUi = this.querySelector('[data-journal-sort-ui]');
    this.sortTrigger = this.querySelector('[data-journal-sort-trigger]');
    this.sortCurrent = this.querySelector('[data-journal-sort-current]');
    this.sortMenu = this.querySelector('[data-journal-sort-menu]');
    this.sortOptions = Array.from(this.querySelectorAll('[data-journal-sort-option]'));
    this.emptyState = this.querySelector('[data-journal-empty]');
    this.pagination = this.querySelector('[data-journal-pagination]');
    this.debounceTimer = null;
  }

  connectedCallback() {
    if (!this.grid) return;

    const params = new URLSearchParams(window.location.search);
    this.searchInput.value = params.get('q') || '';
    this.sortInput.value = params.get('sort') === 'oldest' ? 'oldest' : 'newest';
    this.syncSortUi();

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.render(1, true);
    });

    this.searchInput.addEventListener('input', () => {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => this.render(1, true), 180);
    });

    this.sortInput.addEventListener('change', () => {
      this.syncSortUi();
      this.render(1, true);
    });

    if (this.sortTrigger && this.sortMenu) {
      this.sortTrigger.addEventListener('click', () => this.toggleSortMenu());
      this.sortOptions.forEach((option) => {
        option.addEventListener('click', () => this.setSort(option.dataset.journalSortOption));
      });
      document.addEventListener('click', (event) => {
        if (!this.sortUi.contains(event.target)) this.closeSortMenu();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeSortMenu();
      });
    }

    window.addEventListener('popstate', () => this.syncFromUrl());

    // Use a dedicated `jpage` param so client-side pagination never collides with Shopify's
    // {% paginate %} (which reads `page`) on a reload/shared link.
    this.render(Number(params.get('jpage')) || 1, false);
  }

  syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    this.searchInput.value = params.get('q') || '';
    this.sortInput.value = params.get('sort') === 'oldest' ? 'oldest' : 'newest';
    this.syncSortUi();
    // Use a dedicated `jpage` param so client-side pagination never collides with Shopify's
    // {% paginate %} (which reads `page`) on a reload/shared link.
    this.render(Number(params.get('jpage')) || 1, false);
  }

  setSort(value) {
    this.sortInput.value = value === 'oldest' ? 'oldest' : 'newest';
    this.syncSortUi();
    this.closeSortMenu();
    this.render(1, true);
  }

  syncSortUi() {
    if (!this.sortCurrent || !this.sortInput) return;

    const value = this.sortInput.value === 'oldest' ? 'oldest' : 'newest';
    const currentOption = this.sortInput.querySelector(`option[value="${value}"]`);
    this.sortCurrent.textContent = currentOption ? currentOption.textContent : value;

    this.sortOptions.forEach((option) => {
      option.hidden = false;
      option.setAttribute(
        'aria-checked',
        option.dataset.journalSortOption === value ? 'true' : 'false'
      );
    });
  }

  toggleSortMenu() {
    const isOpen = !this.sortMenu.hidden;
    isOpen ? this.closeSortMenu() : this.openSortMenu();
  }

  openSortMenu() {
    this.sortMenu.hidden = false;
    this.sortTrigger.setAttribute('aria-expanded', 'true');
  }

  closeSortMenu() {
    if (!this.sortMenu || !this.sortTrigger) return;
    this.sortMenu.hidden = true;
    this.sortTrigger.setAttribute('aria-expanded', 'false');
  }

  getFilteredCards() {
    const query = this.searchInput.value.trim().toLowerCase();
    const sort = this.sortInput.value;

    return this.cards
      .filter((card) => !query || card.dataset.search.includes(query))
      .sort((a, b) => {
        const left = Number(a.dataset.published) || 0;
        const right = Number(b.dataset.published) || 0;
        return sort === 'oldest' ? left - right : right - left;
      });
  }

  updateUrl(page) {
    const params = new URLSearchParams(window.location.search);
    const query = this.searchInput.value.trim();
    const sort = this.sortInput.value;

    query ? params.set('q', query) : params.delete('q');
    sort === 'oldest' ? params.set('sort', 'oldest') : params.delete('sort');
    page > 1 ? params.set('jpage', String(page)) : params.delete('jpage');

    const queryString = params.toString();
    const url = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.history.pushState({}, '', url);
  }

  render(requestedPage = 1, shouldUpdateUrl = true) {
    const filteredCards = this.getFilteredCards();
    const totalPages = Math.max(1, Math.ceil(filteredCards.length / this.pageSize));
    const page = Math.min(Math.max(requestedPage, 1), totalPages);
    const start = (page - 1) * this.pageSize;
    const visibleCards = new Set(filteredCards.slice(start, start + this.pageSize));

    this.cards.forEach((card) => {
      card.classList.remove('journal-card-shell--initial-hidden');
      card.hidden = !visibleCards.has(card);
    });

    filteredCards.forEach((card) => this.grid.appendChild(card));
    this.emptyState.hidden = filteredCards.length > 0;
    this.renderPagination(page, totalPages, filteredCards.length);

    if (shouldUpdateUrl) this.updateUrl(page);
  }

  renderPagination(currentPage, totalPages, resultCount) {
    // The nav is only mounted when pagination can appear (see main-blog.liquid); bail if absent.
    if (!this.pagination) return;
    this.pagination.innerHTML = '';
    const isSearching = this.searchInput.value.trim().length > 0;
    const visiblePageCount = isSearching ? totalPages : Math.max(totalPages, this.mockPaginationPages);
    this.pagination.hidden = resultCount <= this.pageSize && visiblePageCount <= 1;

    if (this.pagination.hidden) return;

    if (currentPage > 1) {
      const previousButton = this.createArrowButton(currentPage - 1, 'Previous page', false, 'previous');
      this.pagination.appendChild(previousButton);
    }

    for (let page = 1; page <= visiblePageCount; page += 1) {
      const isPreviewOnly = page > totalPages;
      const button = this.createPageButton(String(page), page, false, `Page ${page}`, isPreviewOnly);
      if (page === currentPage) button.setAttribute('aria-current', 'page');
      this.pagination.appendChild(button);
    }

    const nextButton = this.createArrowButton(
      currentPage + 1,
      'Next page',
      currentPage === totalPages,
      'next'
    );
    this.pagination.appendChild(nextButton);
  }

  createArrowButton(page, ariaLabel, previewOnly, direction) {
    const button = this.createPageButton('', page, false, ariaLabel, previewOnly);
    button.classList.add('ombraz-journal__page-button--arrow', `ombraz-journal__page-button--${direction}`);
    button.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="17" viewBox="0 0 10 17" fill="none" aria-hidden="true" focusable="false">
        <path d="M1.18115 15.3562L8.26865 8.2687L1.18115 1.1812" stroke="#57575C" stroke-width="2.3625" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    return button;
  }

  createPageButton(label, page, disabled, ariaLabel, previewOnly = false) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ombraz-journal__page-button';
    button.textContent = label;
    button.disabled = disabled;
    button.setAttribute('aria-label', ariaLabel);
    if (previewOnly) {
      button.classList.add('ombraz-journal__page-button--preview');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.addEventListener('click', () => this.render(page, true));
    }
    return button;
  }
}

customElements.define('ombraz-journal', OmbrazJournal);
